import { db } from '../firebase';
import { collection, doc, getDocs, getDoc, setDoc, deleteDoc, query, where, orderBy, serverTimestamp } from 'firebase/firestore';

/**
 * Workflow Service - Manages workflow persistence in Firestore
 * Collection: MarketGenie_tenants/{tenantId}/workflows/{workflowId}
 */
class WorkflowService {
  constructor() {
    this.collectionName = 'workflows';
  }

  /**
   * Get all workflows for a tenant
   * @param {string} tenantId - The tenant ID
   * @returns {Promise<Array>} Array of workflow objects
   */
  async getWorkflows(tenantId) {
    if (!tenantId) {
      console.error('❌ WorkflowService: tenantId is required');
      return { success: false, error: 'Tenant ID is required', data: [] };
    }

    try {
      console.log(`📋 Loading workflows for tenant: ${tenantId}`);
      
      const workflowsRef = collection(db, 'MarketGenie_tenants', tenantId, this.collectionName);
      const q = query(workflowsRef, orderBy('updatedAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const workflows = [];
      querySnapshot.forEach((doc) => {
        workflows.push({
          id: doc.id,
          ...doc.data()
        });
      });

      console.log(`✅ Loaded ${workflows.length} workflows`);
      return { success: true, data: workflows };
    } catch (error) {
      console.error('❌ Error loading workflows:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  /**
   * Get a single workflow by ID
   * @param {string} tenantId - The tenant ID
   * @param {string} workflowId - The workflow ID
   * @returns {Promise<Object>} Workflow object
   */
  async getWorkflow(tenantId, workflowId) {
    if (!tenantId || !workflowId) {
      console.error('❌ WorkflowService: tenantId and workflowId are required');
      return { success: false, error: 'Tenant ID and Workflow ID are required' };
    }

    try {
      const workflowRef = doc(db, 'MarketGenie_tenants', tenantId, this.collectionName, workflowId);
      const workflowDoc = await getDoc(workflowRef);
      
      if (workflowDoc.exists()) {
        console.log(`✅ Loaded workflow: ${workflowId}`);
        return { 
          success: true, 
          data: {
            id: workflowDoc.id,
            ...workflowDoc.data()
          }
        };
      } else {
        console.log(`⚠️ Workflow not found: ${workflowId}`);
        return { success: false, error: 'Workflow not found' };
      }
    } catch (error) {
      console.error('❌ Error loading workflow:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Save a workflow (create or update)
   * @param {string} tenantId - The tenant ID
   * @param {Object} workflowData - The workflow data
   * @returns {Promise<Object>} Result object
   */
  async saveWorkflow(tenantId, workflowData) {
    if (!tenantId) {
      console.error('❌ WorkflowService: tenantId is required');
      return { success: false, error: 'Tenant ID is required' };
    }

    if (!workflowData.name || !workflowData.name.trim()) {
      return { success: false, error: 'Workflow name is required' };
    }

    try {
      const workflowId = workflowData.id || `workflow-${Date.now()}`;
      const workflowRef = doc(db, 'MarketGenie_tenants', tenantId, this.collectionName, workflowId);
      
      // Check if workflow already exists
      const existingDoc = await getDoc(workflowRef);
      const isUpdate = existingDoc.exists();
      
      const workflowToSave = {
        name: workflowData.name,
        nodes: workflowData.nodes || [],
        edges: workflowData.edges || [],
        status: workflowData.status || 'draft', // draft, active, paused
        createdAt: isUpdate ? existingDoc.data().createdAt : serverTimestamp(),
        updatedAt: serverTimestamp(),
        // Optional fields
        description: workflowData.description || '',
        triggerType: workflowData.triggerType || null,
        isActive: workflowData.isActive !== undefined ? workflowData.isActive : true,
      };

      await setDoc(workflowRef, workflowToSave, { merge: true });

      console.log(`✅ Workflow ${isUpdate ? 'updated' : 'created'}: ${workflowId}`);
      return { 
        success: true, 
        data: {
          id: workflowId,
          ...workflowToSave
        },
        isUpdate
      };
    } catch (error) {
      console.error('❌ Error saving workflow:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Delete a workflow
   * @param {string} tenantId - The tenant ID
   * @param {string} workflowId - The workflow ID
   * @returns {Promise<Object>} Result object
   */
  async deleteWorkflow(tenantId, workflowId) {
    if (!tenantId || !workflowId) {
      console.error('❌ WorkflowService: tenantId and workflowId are required');
      return { success: false, error: 'Tenant ID and Workflow ID are required' };
    }

    try {
      const workflowRef = doc(db, 'MarketGenie_tenants', tenantId, this.collectionName, workflowId);
      await deleteDoc(workflowRef);

      console.log(`✅ Workflow deleted: ${workflowId}`);
      return { success: true };
    } catch (error) {
      console.error('❌ Error deleting workflow:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update workflow status (active/paused/draft)
   * @param {string} tenantId - The tenant ID
   * @param {string} workflowId - The workflow ID
   * @param {string} status - New status
   * @returns {Promise<Object>} Result object
   */
  async updateWorkflowStatus(tenantId, workflowId, status) {
    if (!tenantId || !workflowId) {
      return { success: false, error: 'Tenant ID and Workflow ID are required' };
    }

    if (!['draft', 'active', 'paused'].includes(status)) {
      return { success: false, error: 'Invalid status. Must be: draft, active, or paused' };
    }

    try {
      const workflowRef = doc(db, 'MarketGenie_tenants', tenantId, this.collectionName, workflowId);
      await setDoc(workflowRef, {
        status,
        isActive: status === 'active',
        updatedAt: serverTimestamp()
      }, { merge: true });

      console.log(`✅ Workflow status updated to: ${status}`);
      return { success: true };
    } catch (error) {
      console.error('❌ Error updating workflow status:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get active workflows (for execution engine)
   * @param {string} tenantId - The tenant ID
   * @returns {Promise<Array>} Array of active workflow objects
   */
  async getActiveWorkflows(tenantId) {
    if (!tenantId) {
      return { success: false, error: 'Tenant ID is required', data: [] };
    }

    try {
      const workflowsRef = collection(db, 'MarketGenie_tenants', tenantId, this.collectionName);
      const q = query(workflowsRef, where('isActive', '==', true));
      const querySnapshot = await getDocs(q);
      
      const workflows = [];
      querySnapshot.forEach((doc) => {
        workflows.push({
          id: doc.id,
          ...doc.data()
        });
      });

      console.log(`✅ Loaded ${workflows.length} active workflows`);
      return { success: true, data: workflows };
    } catch (error) {
      console.error('❌ Error loading active workflows:', error);
      return { success: false, error: error.message, data: [] };
    }
  }
}

// Export singleton instance
const workflowService = new WorkflowService();
export default workflowService;
